import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { toast, ToastContainer } from 'react-toastify';
import CreateLandModel from './create-land-model';
import { useDebounce } from '../../hooks/useDebounce';
import { useDispatch } from 'react-redux';
import { isVisible } from 'slices/loader/reducer';
import {
  exportLands,
  getLandsByRegistrationId,
  getUnregisteredLandsByOrganizationId,
  deleteLand,
  uploadLands,
} from 'services/lands';
import '../../assets/scss/theme/_button.scss';
import { generateFilename } from 'utility/filenameGenerator';
import { getNavigationBreadcrumbs } from 'utility/breadcrumbUtils';
import BulkExcelActions, {
  BulkExcelLoadingOverlay,
} from 'Components/Common/BulkExcelActions';
import { useBulkExcelOperations } from 'utility/excelBulkOperations';

const Lands = () => {
  const { id, trustRegId, registrationId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const mazarName = searchParams.get('mazar_name') || '';
  const category = searchParams.get('category') || '';
  const trustRegNumber = searchParams.get('trustRegNumber')?.trim() || '';
  const trustRegNumberQuery = trustRegNumber
    ? `&trustRegNumber=${encodeURIComponent(trustRegNumber)}`
    : '';
  const trustRegIdQuery = trustRegId
    ? `&trustRegId=${encodeURIComponent(trustRegId)}`
    : '';
  const breadCrumbItems = getNavigationBreadcrumbs({
    organizationId: id || '',
    mazarName,
    category,
    trustRegNumber,
    trustRegId,
  });

  if (breadCrumbItems.length) {
    breadCrumbItems[breadCrumbItems.length - 1].isActive = true;
  }

  const [lands, setLands] = useState<any[]>([]);
  const [modal, setModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setEditData(null);
    } else {
      setModal(true);
    }
  }, [modal]);

  const loadData = async () => {
    dispatch(isVisible(true));
    try {
      const result =
        category === 'unregistered'
          ? await getUnregisteredLandsByOrganizationId(id as string)
          : await getLandsByRegistrationId(trustRegId as string);
      if (result?.data?.length) {
        const list = result?.data?.map(land => land);
        setLands(list);
      } else {
        setLands([]);
      }
    } catch (e: any) {
      toast.error(`Error loading lands: ${e}`);
    } finally {
      dispatch(isVisible(false));
    }
  };

  const { loading, handleExport, handleUpload } = useBulkExcelOperations({
    exportFile: () =>
      exportLands(
        Number(id),
        category,
        registrationId ? Number(registrationId) : undefined,
      ),
    getExportFilename: () =>
      generateFilename({
        organizationName: mazarName || 'Mazar',
        category: category || 'unregistered',
        trustRegNumber: trustRegNumber || 'Orphan',
      }),
    uploadFile: uploadLands,
    onUploadSuccess: () => loadData(),
  });

  const handleDelete = async (landId: number) => {
    if (!window.confirm('Are you sure you want to delete this land record?'))
      return;
    dispatch(isVisible(true));
    try {
      await deleteLand(landId);
      toast.success('Land deleted');
      loadData();
    } catch (e: any) {
      toast.error(`Delete failed: ${e}`);
    } finally {
      dispatch(isVisible(false));
    }
  };

  useEffect(() => {
    loadData();
  }, [debouncedSearchQuery]);

  return (
    <React.Fragment>
      {loading && <BulkExcelLoadingOverlay />}
      <div className="page-content">
        <ToastContainer closeButton={false} limit={1} />
        <BreadCrumb title="Lands" breadcrumbItems={breadCrumbItems} />
        <Row className="g-4 mb-3">
          <div className="col-sm-auto">
            <div>
              <button
                type="button"
                className="btn btn-primary add-btn"
                onClick={toggle}
              >
                <i className="ri-add-line align-bottom me-1"></i> Add Land
              </button>
            </div>

            <BulkExcelActions
              helpTargetId="lands-bulk-upload-help"
              onDownload={handleExport}
              onUpload={handleUpload}
              disabled={loading}
              downloadDisabled={loading || lands.length < 1}
            />
          </div>
          <div className="col-sm-3 ms-auto">
            <div className="d-flex justify-content-sm-end gap-2">
              <div className="search-box ms-2 col-sm-10">
                <Input
                  type="text"
                  className="form-control"
                  placeholder="Search Land"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                <i className="ri-search-line search-icon"></i>
              </div>
            </div>
          </div>
        </Row>
        <Row>
          {lands.length === 0 && (
            <p className="text-center mt-4">No land records found</p>
          )}
          {lands.map((item, key) => (
            <Col xxl={4} sm={6} key={key} className="project-card">
              <Card className="card-height-100 position-relative">
                <UncontrolledDropdown className="position-absolute end-0 me-2 mt-2">
                  <DropdownToggle
                    tag="button"
                    className="btn btn-sm btn-light"
                    aria-expanded="false"
                  >
                    <i className="ri-more-2-fill"></i>
                  </DropdownToggle>
                  <DropdownMenu className="dropdown-menu-end">
                    <DropdownItem
                      onClick={() => {
                        setEditData(item);
                        setModal(true);
                      }}
                    >
                      Edit
                    </DropdownItem>
                    <DropdownItem
                      className="text-danger"
                      onClick={() => handleDelete(item.id)}
                    >
                      Delete
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
                <CardBody>
                  <h6
                    className="mb-2 cursor-pointer text-truncate"
                    onClick={() =>
                      navigate(
                        `/organizations/${id}/lands/${item.id}?mazar_name=${mazarName}&category=${category}${trustRegNumberQuery}&surveyNumber=${item.surveyNumber}${trustRegIdQuery}`,
                      )
                    }
                  >
                    Survey No: {item.surveyNumber}
                  </h6>
                  <p
                    className="text-muted mb-1 text-truncate"
                    title={item.areaSqMt}
                  >
                    Area: {item.areaSqMt} m²
                  </p>
                  <p
                    className="text-muted mb-0 text-truncate"
                    title={item.propertyAddress}
                  >
                    Address: {item.propertyAddress}
                  </p>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        <CreateLandModel
          isOpen={modal}
          toggle={toggle}
          organizationId={Number(id)}
          organizationName={mazarName || ''}
          trustRegId={Number(trustRegId)}
          type={category}
          onSuccess={loadData}
          editData={editData}
        />
      </div>
    </React.Fragment>
  );
};

export default Lands;
